import { useState } from "react";
import { Pencil, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { webApi } from "@/lib/webApi";
import { MediaPicker, HeroTextFields } from "@/components/admin/MediaPicker";
import { ScheduleManager } from "@/components/admin/ScheduleManager";

const TEXT_KEYS = ["headline", "subtext", "button_text"];
const errMsg = (e) => e?.response?.data?.detail || "Something went wrong. Please try again.";

export const HeroStudio = ({ hero, onSaved, refresh }) => {
  const [open, setOpen] = useState(false);
  const [media, setMedia] = useState(null);
  const [text, setText] = useState({});
  const [baseText, setBaseText] = useState({});
  const [busy, setBusy] = useState(false);

  const openStudio = async () => {
    setMedia(null);
    setOpen(true);
    try {
      const { data } = await webApi.get("/hero", { params: { base: true } });
      const t = Object.fromEntries(TEXT_KEYS.map((k) => [k, data[k] || ""]));
      setBaseText({ ...t, hasMedia: !!data.url });
      setText(t);
    } catch {}
  };

  const textChanged = TEXT_KEYS.some((k) => (text[k] || "").trim() !== (baseText?.[k] || ""));

  const save = async () => {
    setBusy(true);
    try {
      let data;
      if (textChanged) data = (await webApi.put("/hero/text", Object.fromEntries(TEXT_KEYS.map((k) => [k, text[k]?.trim() || null])))).data;
      if (media) {
        const form = new FormData();
        form.append("file", media.file);
        data = (await webApi.post("/hero", form)).data;
      }
      onSaved(data);
      toast.success(hero?.schedule_id ? "Saved — it will show once the scheduled banner ends" : "Hero updated for everyone");
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    setBusy(true);
    try {
      onSaved((await webApi.delete("/hero")).data);
      toast.success("Default photo restored");
      setOpen(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="hero-edit-button"
        onClick={openStudio}
        className="absolute top-[120px] right-4 sm:top-28 sm:right-8 lg:right-12 z-10 flex items-center gap-2 bg-[#0F0F0F]/70 backdrop-blur-md border border-[#C5A059]/70 text-[#C5A059] px-4 py-2.5 text-[10px] tracking-[0.3em] uppercase hover:bg-[#C5A059] hover:text-[#0F0F0F] transition-colors"
      >
        <Pencil className="h-3.5 w-3.5" /> Edit hero
      </button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) refresh(); }}>
        <DialogContent data-testid="hero-edit-dialog" className="rounded-none max-w-lg bg-[#FBF9F5] max-h-[92vh] overflow-y-auto">
          <DialogTitle className="font-display text-2xl">Hero banner</DialogTitle>
          <DialogDescription className="text-sm text-[#71717A]">Changes go live for every visitor as soon as you save.</DialogDescription>
          {hero?.schedule_id && (
            <p data-testid="hero-schedule-live-note" className="text-xs bg-[#C5A059]/15 border border-[#C5A059]/40 px-3 py-2">
              A scheduled banner is live until {new Date(hero.ends_at).toLocaleString([], { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}. Changes to the main banner show after it ends.
            </p>
          )}
          <Tabs defaultValue="main">
            <TabsList className="rounded-none w-full bg-[#F5EFE6]">
              <TabsTrigger value="main" data-testid="hero-tab-main" className="rounded-none flex-1 text-xs tracking-[0.2em] uppercase">Main banner</TabsTrigger>
              <TabsTrigger value="schedule" data-testid="hero-tab-schedule" className="rounded-none flex-1 text-xs tracking-[0.2em] uppercase">Scheduled</TabsTrigger>
            </TabsList>
            <TabsContent value="main" className="space-y-5 pt-3">
              <MediaPicker value={media} onChange={setMedia} />
              <HeroTextFields value={text} onChange={setText} />
              <div className="flex gap-3">
                {baseText?.hasMedia && (
                  <button data-testid="hero-reset-button" onClick={restore} disabled={busy} className="flex items-center gap-2 px-4 border border-[#0F0F0F]/30 text-[10px] tracking-[0.2em] uppercase hover:border-[#0F0F0F] disabled:opacity-40">
                    <RotateCcw className="h-3.5 w-3.5" /> Default photo
                  </button>
                )}
                <button data-testid="hero-save-button" onClick={save} disabled={(!media && !textChanged) || busy} className="flex-1 bg-[#0F0F0F] text-[#C5A059] py-3 text-xs tracking-[0.3em] uppercase disabled:opacity-40">
                  {busy ? "Saving…" : "Save & publish"}
                </button>
              </div>
            </TabsContent>
            <TabsContent value="schedule" className="pt-3">
              <ScheduleManager onChanged={refresh} />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
};
